/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { R } from 'retemplate';
import { isInt, compareBigInts, normalizeString, OFFSETTIME_HASH_SEED } from './internal/core';
import { getTime, getTimePart, TimePart, NANOSECONDS_PER_DAY } from './internal/time';
import { getTimeOfInstant, adjustInstant, getInstant } from './internal/instant';
import { TIME, OFFSET, TICKS } from './internal/symbols';
import { NANOS_OF_DAY } from './internal/types';
import { OFFSET_TIME_EXTENDED, OFFSET_TIME_BASIC } from './internal/patterns';
import { Temporal, Components, TimeDelta, TimeUnit } from './types';
import { ZoneOffset } from './Zone';
import { Clock } from './Clock';
import { CivilDate } from './CivilDate';
import { CivilTime } from './CivilTime';
import { ZonedDateTime } from './ZonedDateTime';
import { Instant } from './Instant';
import { Duration } from './Duration';
import { Equatable, Comparable, Equaler } from '@esfx/equatable';

const offsetTimeExtendedRegExp = new RegExp(R`^${OFFSET_TIME_EXTENDED}$`);
const offsetTimeBasicRegExp = new RegExp(R`^${OFFSET_TIME_BASIC}$`);

export class OffsetTime implements Temporal<OffsetTime>, Equatable, Comparable {
    /* @internal */ private [OFFSET]: ZoneOffset;
    /* @internal */ private [TIME]: NANOS_OF_DAY;

    constructor(offset: ZoneOffset, hour: number, minute: number, second: number = 0, millisecond: number = 0, nanosecond: number = 0) {
        if (!isInt(hour) || hour < 0 || hour > 24) throw new RangeError(`The value ${hour} is not a valid hour.`);
        if (!isInt(minute) || minute < 0 || minute > 59) throw new RangeError(`The value ${minute} is not a valid minute.`);
        if (!isInt(second) || second < 0 || second > 59) throw new RangeError(`The value ${second} is not a valid second.`);
        if (!isInt(millisecond) || millisecond < 0 || millisecond > 1e3) throw new RangeError(`The value ${millisecond} is not a valid millisecond.`);
        if (!isInt(nanosecond) || nanosecond < 0 || nanosecond > 1e6) throw new RangeError(`The value ${nanosecond} is not a valid nanosecond.`);
        if (hour === 24 && minute !== 0) throw new RangeError(`The value ${minute} is not a valid minute of hour ${hour}.`);
        if (hour === 24 && second !== 0) throw new RangeError(`The value ${second} is not a valid second of hour ${hour}.`);
        if (hour === 24 && millisecond !== 0) throw new RangeError(`The value ${millisecond} is not a valid second of hour ${millisecond}.`);
        if (hour === 24 && nanosecond !== 0) throw new RangeError(`The value ${nanosecond} is not a valid second of hour ${nanosecond}.`);
        this[OFFSET] = offset;
        this[TIME] = getTime(hour, minute, second, millisecond, nanosecond);
    }

    get offset() { return this[OFFSET]; }
    get offsetSeconds() { return this.offset.offsetSeconds; }
    get offsetString() { return this.offset.offsetString; }
    get hour() { return this[TIME] === NANOSECONDS_PER_DAY ? 24 : getTimePart(this[TIME], TimePart.hour); }
    get minute() { return getTimePart(this[TIME], TimePart.minute); }
    get second() { return getTimePart(this[TIME], TimePart.second); }
    get millisecond() { return getTimePart(this[TIME], TimePart.millisecond); }
    get nanosecond() { return getTimePart(this[TIME], TimePart.nanosecond); }

    plus(deltas?: Components<TimeDelta>) {
        return this
            .toCivilTime()
            .plus(deltas)
            .withOffset(this.offset);
    }

    minus(deltas?: Components<TimeDelta>) {
        return this
            .toCivilTime()
            .minus(deltas)
            .withOffset(this.offset);
    }

    with(components?: Components<TimeUnit>) {
        return this
            .toCivilTime()
            .with(components)
            .withOffset(this.offset);
    }

    withOffsetSameInstant(offset: ZoneOffset) {
        let time = this[TIME]; // Local to this.offset
        time = adjustTime(time, -this.offsetSeconds); // UTC
        time = adjustTime(time, offset.offsetSeconds); // Local to offset
        return OffsetTime.fromNanoseconds(offset, time);
    }

    withOffsetSameLocal(offset: ZoneOffset) {
        return OffsetTime.fromNanoseconds(offset, this[TIME]);
    }

    withDate(date: CivilDate) {
        if (!(date instanceof CivilDate)) throw new TypeError("CivilDate expected: date");
        return ZonedDateTime.fromCalendarDateTime(
            this.offset,
            date.year,
            date.month,
            date.day,
            this.hour,
            this.minute,
            this.second,
            this.millisecond,
            this.nanosecond);
    }

    until(other: OffsetTime): Duration {
        if (!(other instanceof OffsetTime)) throw new TypeError("OffsetTime expected: other");
        return Duration.fromNanoseconds(other[TIME] - this[TIME]);
    }

    since(other: OffsetTime): Duration {
        if (!(other instanceof OffsetTime)) throw new TypeError("OffsetTime expected: other");
        return other.until(this);
    }

    toCivilTime() {
        return new CivilTime(this.hour, this.minute, this.second, this.millisecond, this.nanosecond);
    }

    equals(other: OffsetTime) {
        return other !== null
            && other !== undefined
            && this[TIME] === other[TIME]
            && this[OFFSET].equals(other[OFFSET]);
    }

    compareTo(other: OffsetTime): number {
        return compareBigInts(this[TIME], other[TIME]);
    }

    toString() {
        return `${this.toCivilTime()}${this.offsetString}`;
    }

    toISOString() {
        return this.toString();
    }

    toJSON() {
        return this.toString();
    }

    static from(offset: ZoneOffset, time: CivilTime) {
        return OffsetTime.fromNanoseconds(offset, time[TIME]);
    }

    /**
     * Creates a OffseTime from the number of nanoseconds since the start of the local day.
     */
    static fromNanoseconds(offset: ZoneOffset, nanoseconds: bigint): OffsetTime {
        if (nanoseconds < 0 || nanoseconds >= NANOSECONDS_PER_DAY) throw new RangeError(`The value ${nanoseconds} is not a valid number of nanoseconds`);
        return Object.create(OffsetTime.prototype, {
            [OFFSET]: { value: offset },
            [TIME]: { value: nanoseconds }
        });
    }

    static fromInstant(offset: ZoneOffset, instant: Instant) {
        let time = getTimeOfInstant(instant[TICKS]); // UTC
        time = adjustTime(time, offset.offsetSeconds); // Local
        return OffsetTime.fromNanoseconds(offset, time);
    }

    static fromString(text: string) {
        const normalText = normalizeString(text);
        const match = offsetTimeExtendedRegExp.exec(normalText) || offsetTimeBasicRegExp.exec(normalText);
        if (match) {
            const { hour, minute, second, millisecond = "", nanosecond = "", offset } = match.groups!;
            const zone = ZoneOffset.fromString(offset);
            return new OffsetTime(zone, +hour, +minute, +second, +millisecond.padEnd(3, "0"), +nanosecond.padEnd(6, "0"));
        }

        throw new Error(`Invalid offset time string '${text}'.`);
    }

    static now(clock: Clock) {
        const instant = clock.currentInstant();
        const offset = clock.zone.getOffset(instant);
        return OffsetTime.fromInstant(offset, instant);
    }

    [Equatable.equals](other: unknown) {
        return other instanceof OffsetTime
            && this.equals(other);
    }

    [Equatable.hash]() {
        let hc = OFFSETTIME_HASH_SEED;
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this[TIME]);
        hc = ((hc << 7) | (hc >>> 25)) ^ this[OFFSET][Equatable.hash]();
        return hc;
    }

    [Comparable.compareTo](other: unknown) {
        if (!(other instanceof OffsetTime)) throw new TypeError();
        return this.compareTo(other);
    }
}

Object.defineProperty(OffsetTime.prototype, Symbol.toStringTag, {
    writable: true,
    configurable: true,
    value: "OffsetTime"
});

function adjustTime(time: NANOS_OF_DAY, offsetSeconds: number) {
    return getTimeOfInstant(adjustInstant(getInstant(0n, time), offsetSeconds));
}
